import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Shield, Settings, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { initGA4, initPostHog, initHubSpot } from "@/lib/analytics";

type ConsentPreferences = {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
};

const CONSENT_KEY = "ng_cookie_consent";

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
    // If user already consented to analytics, initialize GA4
    try {
      const parsed = JSON.parse(stored);
      if (parsed.analytics) { initGA4(); initPostHog(); }
      if (parsed.marketing) { initHubSpot(); }
    } catch {}
  }, []);

  const saveConsent = (prefs: ConsentPreferences) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...prefs, timestamp: new Date().toISOString() }));
    if (prefs.analytics) { initGA4(); initPostHog(); }
    if (prefs.marketing) { initHubSpot(); }
    setVisible(false);
  };

  const acceptAll = () => saveConsent({ necessary: true, analytics: true, marketing: true });
  const rejectOptional = () => saveConsent({ necessary: true, analytics: false, marketing: false });
  const saveCustom = () => saveConsent(preferences);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="container mx-auto max-w-3xl rounded-2xl border border-border bg-card p-5 shadow-2xl md:p-6">
            <div className="flex items-start gap-3">
              <Shield className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="flex-1">
                <h3 className="font-display text-base font-semibold text-foreground">
                  Tu privacidad importa
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Usamos cookies esenciales para el funcionamiento del sitio. Las cookies opcionales nos ayudan a mejorar tu experiencia.{" "}
                  <a href="/politica-de-privacidad" className="underline transition-colors hover:text-foreground">
                    Política de cookies
                  </a>
                </p>

                {/* Detail panel */}
                <AnimatePresence>
                  {showDetails && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-4 space-y-3">
                        {[
                          { key: "necessary" as const, label: "Necesarias", desc: "Imprescindibles para el funcionamiento del sitio (sesión, consentimiento).", locked: true },
                          { key: "analytics" as const, label: "Analíticas (GA4 + PostHog + HubSpot)", desc: "Google Analytics 4, PostHog (EU) y HubSpot para analizar visitas, comportamiento y atribución.", locked: false },
                          { key: "marketing" as const, label: "Marketing (HubSpot)", desc: "Cookies de seguimiento de HubSpot para personalizar contenido y comunicaciones.", locked: false },
                        ].map((cat) => (
                          <label
                            key={cat.key}
                            className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5"
                          >
                            <div>
                              <span className="text-sm font-medium text-foreground">{cat.label}</span>
                              <p className="text-xs text-muted-foreground">{cat.desc}</p>
                            </div>
                            <input
                              type="checkbox"
                              checked={preferences[cat.key]}
                              disabled={cat.locked}
                              onChange={() =>
                                setPreferences((p) => ({ ...p, [cat.key]: !p[cat.key] }))
                              }
                              className="h-4 w-4 accent-primary disabled:opacity-60"
                            />
                          </label>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Actions */}
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={acceptAll}>
                    Aceptar todas
                  </Button>
                  <Button size="sm" variant="outline" onClick={rejectOptional}>
                    Solo necesarias
                  </Button>
                  {showDetails ? (
                    <Button size="sm" variant="secondary" onClick={saveCustom}>
                      Guardar preferencias
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowDetails(true)}
                      className="gap-1"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Personalizar
                    </Button>
                  )}
                </div>
              </div>
              <button
                onClick={rejectOptional}
                className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
